import React, { useEffect, useRef, useState } from "react";
import { Button } from "components/ui/BootstrapCompat";
import { FiArrowDown, FiArrowLeft, FiArrowRight, FiSliders, FiTarget, FiTrash2 } from "react-icons/fi";
import type { AlertImageUploadField, AlertLayoutPreset, FlowContextMenuState, FlowEditorNode, FlowEditorState, FlowGridItem, FlowGridLayouts, FlowNodeKind, ResizeHandle, WidgetAnchor, WidgetConfigState } from "./types";
import { DEFAULT_WIDGET_IMAGE, alertLayoutPresets, getAlertLayoutPresetDefinition, viewportPresets } from "./constants";
import { getAlertPresetGridSize, getCompactNodeWidth, getEditorNodePixels, getEffectiveNodeAnchor, getFittedViewportZoom, getFlowViewport, getGridColumnWidth, getGridConfig, getGridItemPixels, getGridRows, getNodeAnchor, getNodeGridItem, isEnabledFlowNodeKind, isNodeAnchored, positionGridItemByAnchor, resolveGridCollision, setNodeLayout, setNodeLayoutByAnchor } from "./flowUtils";
import { clampValue } from "./widgetUtils";
import { FlowBuilderView } from "./FlowBuilderView";
import { useFlowNodeActions } from "./useFlowNodeActions";

export function FlowBuilder({ flowState, selectedNodeId, config, onChange, onSelectNode }: { flowState: FlowEditorState; selectedNodeId: string; config: WidgetConfigState; onChange: React.Dispatch<React.SetStateAction<FlowEditorState>>; onSelectNode: (nodeId: string) => void }) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    originItem: FlowGridItem;
  } | null>(null);
  const resizeRef = useRef<{
    id: string;
    handle: ResizeHandle;
    startX: number;
    startY: number;
    originItem: FlowGridItem;
  } | null>(null);
  const [isAddObjectOpen, setIsAddObjectOpen] = useState(false);
  const [settingsNodeId, setSettingsNodeId] = useState("");
  const [contextMenu, setContextMenu] = useState<FlowContextMenuState>(null);
  const [imageUploadState, setImageUploadState] = useState<{
    nodeId: string;
    field: AlertImageUploadField;
    progress: number;
    error: string;
  } | null>(null);
  const settingsNode = flowState.nodes.find((node) => node.id === settingsNodeId);
  const viewport = getFlowViewport(flowState.viewport);

  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return undefined;

    const syncViewportZoom = () => {
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      onChange((current) => {
        const currentViewport = getFlowViewport(current.viewport);
        const nextZoom = getFittedViewportZoom(currentViewport, rect.width, rect.height);

        if (Math.abs(currentViewport.zoom - nextZoom) < 0.005) {
          return current;
        }

        return {
          ...current,
          viewport: {
            ...currentViewport,
            zoom: nextZoom,
          },
        };
      });
    };

    syncViewportZoom();

    const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(syncViewportZoom);
    resizeObserver?.observe(element);
    window.addEventListener("resize", syncViewportZoom);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncViewportZoom);
    };
  }, [flowState.viewport.device, flowState.viewport.height, flowState.viewport.width, onChange]);

  useEffect(() => {
    if (!contextMenu) return undefined;

    const closeContextMenu = () => setContextMenu(null);
    const closeContextMenuOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeContextMenu();
    };

    window.addEventListener("click", closeContextMenu);
    window.addEventListener("scroll", closeContextMenu, true);
    document.addEventListener("keydown", closeContextMenuOnEscape);

    return () => {
      window.removeEventListener("click", closeContextMenu);
      window.removeEventListener("scroll", closeContextMenu, true);
      document.removeEventListener("keydown", closeContextMenuOnEscape);
    };
  }, [contextMenu]);

  const { addNode, clearImageUploadState, deleteNode, updateNode, updateNodeData, updateNodeSetting, uploadNodeAlertImage } = useFlowNodeActions({
    config,
    selectedNodeId,
    onChange,
    onSelectNode,
    setContextMenu,
    setImageUploadState,
    setSettingsNodeId,
  });
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const kind = event.dataTransfer.getData("application/x-widget-node") as FlowNodeKind;
    if (!isEnabledFlowNodeKind(kind)) return;

    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;

    addNode(kind, {
      x: Math.max(12, (event.clientX - rect.left) / viewport.zoom - 90),
      y: Math.max(12, (event.clientY - rect.top) / viewport.zoom - 40),
    });
  };

  const handleNodePointerDown = (event: React.PointerEvent<HTMLDivElement>, node: FlowEditorNode) => {
    event.stopPropagation();
    onSelectNode(node.id);

    if (event.button !== 0) return;

    event.currentTarget.setPointerCapture(event.pointerId);

    if (isNodeAnchored(node)) {
      dragRef.current = null;
      return;
    }

    dragRef.current = {
      id: node.id,
      startX: event.clientX,
      startY: event.clientY,
      originItem: getNodeGridItem(node, viewport),
    };
  };

  const handleNodeResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>, node: FlowEditorNode, handle: ResizeHandle) => {
    event.preventDefault();
    event.stopPropagation();
    onSelectNode(node.id);

    const nodeElement = event.currentTarget.parentElement;
    nodeElement?.setPointerCapture(event.pointerId);
    dragRef.current = null;
    resizeRef.current = {
      id: node.id,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      originItem: getNodeGridItem(node, viewport),
    };
  };

  const handleNodePointerMove = (event: React.PointerEvent<HTMLDivElement>, node: FlowEditorNode) => {
    const resizeInfo = resizeRef.current;
    if (resizeInfo && resizeInfo.id === node.id) {
      event.preventDefault();

      const config = getGridConfig(viewport);
      const colWidth = getGridColumnWidth(viewport);
      const deltaW = Math.round((event.clientX - resizeInfo.startX) / viewport.zoom / (colWidth + config.margin));
      const deltaH = Math.round((event.clientY - resizeInfo.startY) / viewport.zoom / (config.rowHeight + config.margin));
      const nextItem = {
        ...resizeInfo.originItem,
        w: resizeInfo.handle === "bottom" ? resizeInfo.originItem.w : resizeInfo.originItem.w + deltaW,
        h: resizeInfo.handle === "right" ? resizeInfo.originItem.h : resizeInfo.originItem.h + deltaH,
      };

      updateNode(node.id, setNodeLayoutByAnchor(node, viewport, nextItem));
      return;
    }

    const dragInfo = dragRef.current;
    if (!dragInfo || dragInfo.id !== node.id) return;

    event.preventDefault();
    const config = getGridConfig(viewport);
    const colWidth = getGridColumnWidth(viewport);
    const deltaX = Math.round((event.clientX - dragInfo.startX) / viewport.zoom / (colWidth + config.margin));
    const deltaY = Math.round((event.clientY - dragInfo.startY) / viewport.zoom / (config.rowHeight + config.margin));
    const nextItem = resolveGridCollision(
      flowState.nodes,
      node.id,
      {
        ...dragInfo.originItem,
        x: dragInfo.originItem.x + deltaX,
        y: dragInfo.originItem.y + deltaY,
      },
      viewport,
    );
    updateNode(node.id, setNodeLayout(node, viewport, nextItem));
  };

  const handleNodePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    resizeRef.current = null;
    dragRef.current = null;
  };

  const handleNodePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    resizeRef.current = null;
    dragRef.current = null;
  };

  const handleNodeContextMenu = (event: React.MouseEvent<HTMLDivElement>, node: FlowEditorNode) => {
    event.preventDefault();
    event.stopPropagation();
    onSelectNode(node.id);
    setContextMenu({
      nodeId: node.id,
      x: Math.min(event.clientX, window.innerWidth - 190),
      y: Math.min(event.clientY, window.innerHeight - 120),
    });
  };

  const setNodeAnchor = (nodeId: string, anchor: WidgetAnchor) => {
    if (anchor === "free") {
      updateNodeSetting(nodeId, "anchor", "free");
      return;
    }

    onChange((current) => {
      const currentViewport = getFlowViewport(current.viewport);

      return {
        ...current,
        nodes: current.nodes.map((node) => {
          if (node.id !== nodeId) return node;
          const anchoredNode = setNodeLayoutByAnchor(node, currentViewport, getNodeGridItem(node, currentViewport), anchor);

          return {
            ...anchoredNode,
            data: {
              ...anchoredNode.data,
              settings: {
                ...(anchoredNode.data.settings || {}),
                anchor,
              },
            },
          };
        }),
      };
    });
  };

  const applyAlertLayoutPreset = (nodeId: string, layoutPreset: AlertLayoutPreset) => {
    const preset = getAlertLayoutPresetDefinition(layoutPreset);

    onChange((current) => {
      const currentViewport = getFlowViewport(current.viewport);

      return {
        ...current,
        nodes: current.nodes.map((node) => {
          if (node.id !== nodeId) return node;

          const anchor = getNodeAnchor(node);
          const nextLayouts = viewportPresets.reduce((layouts, viewportPreset) => {
            const presetViewport = getFlowViewport({
              device: viewportPreset.key,
            });
            const currentItem = getNodeGridItem(node, presetViewport);
            const presetSize = getAlertPresetGridSize(preset.key, presetViewport.device);
            const nextItem = positionGridItemByAnchor(
              {
                ...currentItem,
                w: presetSize.w,
                h: presetSize.h,
              },
              presetViewport,
              anchor,
            );

            return {
              ...layouts,
              [viewportPreset.key]: nextItem,
            };
          }, {} as FlowGridLayouts);
          const currentItem = nextLayouts[currentViewport.device] || getNodeGridItem(node, currentViewport);
          const nextNode = {
            ...node,
            data: {
              ...node.data,
              layouts: {
                ...(node.data.layouts || {}),
                ...nextLayouts,
              },
              settings: {
                ...(node.data.settings || {}),
                layoutPreset: preset.key,
                theme: preset.theme,
                backgroundStyle: "none",
                effect: "none",
                showIcon: preset.showIcon,
              },
            },
          };

          return setNodeLayout(nextNode, currentViewport, currentItem);
        }),
      };
    });
  };

  const stageGridConfig = getGridConfig(viewport);
  const stageGridSize = `${getGridColumnWidth(viewport) + stageGridConfig.margin}px ${stageGridConfig.rowHeight + stageGridConfig.margin}px`;

  return (
    <FlowBuilderView
      canvasRef={canvasRef}
      stageRef={stageRef}
      flowState={flowState}
      selectedNodeId={selectedNodeId}
      viewport={viewport}
      stageGridSize={stageGridSize}
      isAddObjectOpen={isAddObjectOpen}
      settingsNode={settingsNode}
      contextMenu={contextMenu}
      imageUploadState={imageUploadState}
      handleDrop={handleDrop}
      handleNodePointerDown={handleNodePointerDown}
      handleNodePointerMove={handleNodePointerMove}
      handleNodePointerUp={handleNodePointerUp}
      handleNodePointerCancel={handleNodePointerCancel}
      handleNodeResizePointerDown={handleNodeResizePointerDown}
      handleNodeContextMenu={handleNodeContextMenu}
      addNode={addNode}
      deleteNode={deleteNode}
      setIsAddObjectOpen={setIsAddObjectOpen}
      setSettingsNodeId={setSettingsNodeId}
      setContextMenu={setContextMenu}
      applyAlertLayoutPreset={applyAlertLayoutPreset}
      clearImageUploadState={clearImageUploadState}
      setNodeAnchor={setNodeAnchor}
      updateNodeData={updateNodeData}
      updateNodeSetting={updateNodeSetting}
      uploadNodeAlertImage={uploadNodeAlertImage}
      onSelectNode={onSelectNode}
    />
  );
}
